import View                      from '@widesk-ui/components/View';
import SelectPlace               from '@selects/map/SelectPlace';
import currentEditorStore        from '@/TokamapEditor/stores/currentEditorStore';
import Button                    from '@widesk-ui/components/Button';
import PlaceModel                from '@models/map/PlaceModel';
import leftSidebarStore          from '@/TokamapEditor/stores/leftSidebarStore';
import currentProjectEditorStore from '@/TokamapEditor/stores/currentProjectEditorStore';
import { observer }              from 'mobx-react-lite';
import linkPlaceToShape          from '@/TokamapEditor/helpers/linkPlaceToShape';
import { commonValue }           from '@/TokamapEditor/ux/AreaSidebar/multiSelectHelpers';
import useDesignTokens           from '@/hooks/useDesignTokens';
import React                     from 'react';

const AreaPlaceSection = () => <AreaPlaceSectionContent />;

const AreaPlaceSectionContent = observer(() => {
	const shapes = currentEditorStore.sidebarShapes;
	const tokens = useDesignTokens();

	if (!shapes.length) return null;

	const onChange = async (place?: PlaceModel) => {
		if (place) {
			await linkPlaceToShape(shapes.map(s => s.id), place);
		}
	};

	const entityId = commonValue(shapes, s => (s.entity as any)?.entity?.id);

	/**
	 * Le point d'intérêt relié, chargé pour pouvoir ouvrir sa fiche : les formes ne portent que son
	 * id, et la fiche a besoin du modèle (son titre s'affiche dans l'en-tête dès l'ouverture).
	 */
	const [linkedPlace, setLinkedPlace] = React.useState<PlaceModel>();

	React.useEffect(() => {
		if (!entityId) return setLinkedPlace(undefined);

		const place = new PlaceModel();

		place.setId(entityId).fetch()
			.then(() => setLinkedPlace(place))
			.catch(err => console.error('Point d\'intérêt de la forme', err));
	}, [entityId]);

	return (
		<View
			gap="sm"
			p="sm"
			br={8}
			style={{ border: `1px solid ${tokens.border.default}`, background: tokens.surface.elevated }}
		>
			<View color={tokens.text.muted} size={13}>
				Souhaitez-vous utiliser un point d'intérêt déjà existant ou en ajouter un nouveau ?
			</View>

			<SelectPlace
				projectUrn={currentProjectEditorStore.get.project.urn}
				placeholder="Points d'intérêts déjà existants"
				value={entityId ? `$:map:place:${entityId}` : undefined}
				onModelChange={place => onChange(place)}
				filters={{ project: currentProjectEditorStore.get.project.id }}
			/>

			{/* Remplace le crayon que `SelectCollection` posait dans la liste déroulante, qui ouvrait une
			    modale. Même destination, mais dans un panneau de la sidebar — et hors du sélecteur, où il
			    n'apparaissait qu'au survol d'une option. */}
			{!!linkedPlace && (
				<View row>
					<Button
						type="link"
						size="small"
						style={{ padding: 0 }}
						onClick={() => leftSidebarStore.openPlace(linkedPlace)}
					>
						Modifier « {linkedPlace.label} »
					</Button>
				</View>
			)}

			<View row centerV gap="xs">
				<View flex h={1} bg={tokens.border.default} />
				<View size={11.5} bold color={tokens.text.muted}>OU</View>
				<View flex h={1} bg={tokens.border.default} />
			</View>

			{/* Un panneau de la sidebar, plus une modale : l'éditeur passe par ses tiroirs, et celui-ci
			    apporte en prime le choix du type par tuiles et l'enregistrement au fil de l'eau. Le point
			    d'intérêt est relié à la forme dès sa création, et sa fiche reste ouverte pour être
			    complétée — le retour ramène ici. */}
			<Button onClick={() => leftSidebarStore.openNewPlace(place => void onChange(place))}>
				<b>Ajouter un nouveau point d'intérêt</b>
			</Button>
		</View>
	);
});

export default AreaPlaceSection;